import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useLocation } from "wouter";
import { toast } from "sonner";
import {
  Building2, Plus, Settings2, Wifi, ChevronDown, ChevronRight,
  ToggleLeft, ToggleRight, Pencil, Check, X, Shield, Globe, Palette,
  Zap, Package, Star, Crown, AlertCircle, Fish, Database, LayoutDashboard,
  UserPlus, Copy, Mail, ExternalLink, Eye, BookOpen, Trash2, Sparkles,
  Plug, Instagram, Linkedin, Youtube, BarChart3
} from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
// (removido) GestaoAPIsEditor — só RELGOV
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NewClientWizard } from "@/components/NewClientWizard";

const ALL_MODULES = [
  // Principal
  { key: "dashboard",         label: "Dashboard",                section: "Principal" },
  { key: "calendario",        label: "Calendário Editorial",      section: "Principal" },
  { key: "pautas",            label: "Gestão de Pautas",          section: "Principal" },
  { key: "ideias",            label: "Banco de Ideias",           section: "Principal" },
  // Inteligência
  { key: "metricas",          label: "Métricas & KPIs",           section: "Inteligência" },
  { key: "insights",          label: "Insights",                  section: "Inteligência" },
  // Conteúdo
  { key: "editorial",         label: "Linha Editorial",           section: "Conteúdo" },
  { key: "assets",            label: "Biblioteca de Assets",      section: "Conteúdo" },
  { key: "ceo_react",         label: "CEO React",                 section: "Conteúdo" },
  { key: "utms",              label: "UTM Generator",             section: "Conteúdo" },
];

const ALL_NETWORKS = [
  { key: "linkedin",  label: "LinkedIn",  color: "#0A66C2" },
  { key: "instagram", label: "Instagram", color: "#E1306C" },
  { key: "tiktok",    label: "TikTok",    color: "#010101" },
  { key: "youtube",   label: "YouTube",   color: "#FF0000" },
];

const PLANOS = [
  { value: "starter",    label: "Starter",    icon: Star,    color: "#7e7f7d",
    modules: ["dashboard","pautas","calendario","ideias"] },
  { value: "pro",        label: "Pro",        icon: Package, color: "#c14727",
    modules: ["dashboard","pautas","calendario","ideias","metricas","insights","editorial","assets"] },
  { value: "enterprise", label: "Enterprise", icon: Crown,   color: "#7c3aed",
    modules: ["dashboard","pautas","calendario","ideias","metricas","insights","editorial","assets","ceo_react","utms"] },
];

const PLANO_COLORS: Record<string, string> = {
  starter:    "bg-gray-100 text-gray-700",
  pro:        "bg-orange-100 text-orange-700",
  enterprise: "bg-purple-100 text-purple-700",
};

type Client = {
  id: number; nome: string; slug: string; subdomain: string | null;
  logo: string | null; logoUrl: string | null; logoNegativoUrl: string | null;
  cor: string | null; primaryColor: string | null;
  descricao: string | null; contato: string | null; plano: string | null; ativo: boolean;
};

function ClientCard({
  client, expanded, onToggle, editingId, editNome, setEditingId, setEditNome,
  onUpdateNome, onToggleAtivo, setModule, setNetwork, onUpdateHomeModule,
}: {
  client: Client; expanded: boolean; onToggle: () => void;
  editingId: number | null; editNome: string;
  setEditingId: (id: number | null) => void; setEditNome: (v: string) => void;
  onUpdateNome: (id: number, nome: string) => void;
  onToggleAtivo: (id: number, ativo: boolean) => void;
  setModule: (clientId: number, modulo: string, ativo: boolean) => void;
  setNetwork: (clientId: number, rede: string, ativo: boolean) => void;
  onUpdateHomeModule?: (id: number, homeModule: string) => void;
}) {
  const { data: modules = [] } = trpc.clients.getModules.useQuery({ clientId: client.id }, { enabled: expanded });
  const { data: networks = [] } = trpc.clients.getNetworks.useQuery({ clientId: client.id }, { enabled: expanded });
  const activeModules = new Set(modules.filter(m => m.ativo).map(m => m.modulo));
  const activeNetworks = new Set(networks.filter(n => n.ativo).map(n => n.rede));
  const color = client.primaryColor || client.cor || "#c14727";
  const plano = client.plano || "starter";
  return (
    <div className={`rounded-xl border border-border bg-card transition-all ${!client.ativo ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={onToggle}>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
          style={{ backgroundColor: color }}>
          {client.nome.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          {editingId === client.id ? (
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <Input value={editNome} onChange={e => setEditNome(e.target.value)} className="h-7 text-sm py-0" autoFocus />
              <button className="text-green-600 hover:text-green-700" onClick={() => { onUpdateNome(client.id, editNome); setEditingId(null); }}><Check className="w-4 h-4" /></button>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm truncate">{client.nome}</span>
              <button className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={e => { e.stopPropagation(); setEditingId(client.id); setEditNome(client.nome); }}>
                <Pencil className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {client.subdomain && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Globe className="w-3 h-3" />{client.subdomain}.razconsulting.com.br
              </span>
            )}
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${PLANO_COLORS[plano]}`}>{plano}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
          <button onClick={() => onToggleAtivo(client.id, !client.ativo)} className="text-muted-foreground hover:text-foreground">
            {client.ativo ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5" />}
          </button>
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Settings2 className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Módulos</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_MODULES.map(mod => {
                const on = activeModules.has(mod.key);
                return (
                  <button key={mod.key}
                    onClick={() => setModule(client.id, mod.key, !on)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all font-medium ${on
                      ? "border-transparent text-white"
                      : "border-border text-muted-foreground hover:border-foreground/30"
                    }`}
                    style={on ? { backgroundColor: color } : {}}>
                    {mod.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wifi className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Redes Sociais</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_NETWORKS.map(net => {
                const on = activeNetworks.has(net.key);
                return (
                  <button key={net.key}
                    onClick={() => setNetwork(client.id, net.key, !on)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all font-medium ${on
                      ? "border-transparent text-white"
                      : "border-border text-muted-foreground hover:border-foreground/30"
                    }`}
                    style={on ? { backgroundColor: net.color } : {}}>
                    {net.label}
                  </button>
                );
              })}
            </div>
          </div>
          {(client.descricao || client.contato) && (
            <div className="text-xs text-muted-foreground space-y-0.5">
              {client.descricao && <p>{client.descricao}</p>}
              {client.contato && <p>Contato: {client.contato}</p>}
            </div>
          )}
          {onUpdateHomeModule && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <LayoutDashboard className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tela Inicial (home_module)</span>
              </div>
              <Select
                value={(client as any).homeModule ?? ''}
                onValueChange={(v) => onUpdateHomeModule(client.id, v)}
              >
                <SelectTrigger className="h-8 text-xs w-64">
                  <SelectValue placeholder="Selecionar módulo inicial" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_MODULES.map(m => (
                    <SelectItem key={m.key} value={m.key} className="text-xs">{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {/* Chaves de API por cliente */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Chaves de API</span>
            </div>
            {/* <GestaoAPIsEditor clientId={client.id} /> */}
          </div>
          {/* Convidar usuário para este cliente */}
          <InviteClientUserSection clientId={client.id} />
          {/* Ver como cliente */}
          <ViewAsClientButton clientId={client.id} clientNome={client.nome} />
        </div>
      )}
    </div>
  );
}

function InviteClientUserSection({ clientId }: { clientId: number }) {
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [role, setRole] = useState<"admin" | "editor" | "viewer">("viewer");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [emailStatus, setEmailStatus] = useState<"sent" | "no_api_key" | "failed" | null>(null);
  const inviteMutation = trpc.clientAuth.invite.useMutation({
    onSuccess: (data: any) => {
      setInviteLink(data.inviteUrl);
      setEmailStatus(data.emailStatus ?? null);
      // Auto-copy pra clipboard pra garantir
      navigator.clipboard.writeText(data.inviteUrl).catch(() => {});
      if (data.emailStatus === "sent")        toast.success(`Convite gerado e enviado por email pra ${email}. Link copiado.`);
      else if (data.emailStatus === "no_api_key") toast.info("Convite gerado. Email desativado — link copiado pro clipboard.");
      else                                     toast.warning("Convite gerado, mas email FALHOU (Resend domain?). Link copiado, envie manual.");
    },
    onError: (e) => toast.error(e.message),
  });
  const handleInvite = () => {
    if (!email) { toast.error("Informe o e-mail"); return; }
    inviteMutation.mutate({ email, name: nome || email.split("@")[0], clientId, role, origin: window.location.origin });
  };
  const handleCopy = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <UserPlus className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Convidar Usuário</span>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="E-mail (ex: evelyn@abia.org.br)"
            value={email}
            onChange={e => { setEmail(e.target.value); setInviteLink(null); }}
            className="h-8 text-xs flex-1 min-w-48"
          />
          <Input
            placeholder="Nome (opcional)"
            value={nome}
            onChange={e => setNome(e.target.value)}
            className="h-8 text-xs w-40"
          />
          <Select value={role} onValueChange={(v) => setRole(v as any)}>
            <SelectTrigger className="h-8 text-xs w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin" className="text-xs">Admin</SelectItem>
              <SelectItem value="editor" className="text-xs">Editor</SelectItem>
              <SelectItem value="viewer" className="text-xs">Viewer</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={handleInvite}
            disabled={inviteMutation.isPending || !email}
          >
            <Mail className="w-3.5 h-3.5" />
            {inviteMutation.isPending ? "Gerando..." : "Convidar"}
          </Button>
        </div>
        {inviteLink && (
          <>
            {emailStatus === "failed" && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                ⚠ Email FALHOU (Resend domain provavelmente não verificado). Copia o link e envia manual.
              </div>
            )}
            {emailStatus === "no_api_key" && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                ⚠ Email desativado. Copia o link e envia manual.
              </div>
            )}
            <div className={`flex items-center gap-2 p-2.5 rounded-lg border min-w-0 ${
              emailStatus === "sent" ? "bg-green-50 border-green-200" : "bg-muted/50 border-border"
            }`}>
              <ExternalLink className={`w-3.5 h-3.5 flex-shrink-0 ${
                emailStatus === "sent" ? "text-green-600" : "text-muted-foreground"
              }`} />
              {/* min-w-0 + block pra truncate funcionar dentro do flex */}
              <span className="text-xs truncate flex-1 min-w-0 font-mono block">{inviteLink}</span>
              <button onClick={handleCopy} className="text-foreground hover:opacity-70 flex-shrink-0 px-2 py-1 rounded border border-border bg-card flex items-center gap-1 text-[11px] font-semibold">
                {copied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ViewAsClientButton({ clientId, clientNome }: { clientId: number; clientNome: string }) {
  const { setDevClientId, devClientId, isDev } = useTenant();
  const [, navigate] = useLocation();
  const isActive = devClientId === clientId;

  const handleViewAs = () => {
    if (isActive) {
      setDevClientId(null);
      toast.info("Voltando para a visão de admin.");
    } else {
      setDevClientId(clientId);
      toast.success(`Visualizando como cliente: ${clientNome}`);
      navigate("/");
    }
  };

  if (!isDev) return null; // Apenas em dev/preview

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Eye className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Visualizar como Cliente</span>
      </div>
      <Button
        size="sm"
        variant={isActive ? "default" : "outline"}
        className={`h-8 text-xs gap-1.5 ${isActive ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500" : ""}`}
        onClick={handleViewAs}
      >
        <Eye className="w-3.5 h-3.5" />
        {isActive ? `Saindo de "${clientNome}"` : `Ver como "${clientNome}"`}
      </Button>
      {isActive && (
        <p className="text-xs text-amber-600 mt-1.5">
          Você está visualizando o painel como este cliente. Todos os dados e módulos refletem a configuração do cliente.
        </p>
      )}
    </div>
  );
}

type NewForm = {
  nome: string; slug: string; subdomain: string;
  primaryColor: string; descricao: string; contato: string;
  plano: "starter" | "pro" | "enterprise" | "gir";
};
const EMPTY_FORM: NewForm = {
  nome: "", slug: "", subdomain: "", primaryColor: "#c14727",
  descricao: "", contato: "", plano: "starter",
};

// Group modules by section for display
const MODULES_BY_SECTION = ALL_MODULES.reduce((acc, mod) => {
  const sec = (mod as { key: string; label: string; section?: string }).section ?? "Outros";
  if (!acc[sec]) acc[sec] = [];
  acc[sec].push(mod);
  return acc;
}, {} as Record<string, typeof ALL_MODULES>);

export default function Admin() {
  const { session } = useAuth();
  const user = session;
  const utils = trpc.useUtils();
  const { data: clients = [], isLoading } = trpc.clients.list.useQuery();
  const createClient = trpc.clients.create.useMutation({
    onSuccess: () => {
      utils.clients.list.invalidate();
      toast.success("Cliente criado com sucesso!");
      setShowNew(false);
      setNewForm(EMPTY_FORM);
    },
    onError: (e) => toast.error(e.message),
  });
  const updateClient = trpc.clients.update.useMutation({
    onSuccess: () => { utils.clients.list.invalidate(); toast.success("Atualizado!"); },
  });
  const setModule = trpc.clients.setModule.useMutation({
    onSuccess: (_, vars) => {
      utils.clients.getModules.invalidate({ clientId: vars.clientId });
      utils.clients.getActiveConfig.invalidate();
      toast.success(vars.ativo ? "Módulo ativado" : "Módulo desativado");
    },
    onError: () => toast.error("Erro ao salvar módulo"),
  });
  const setNetwork = trpc.clients.setNetwork.useMutation({
    onSuccess: (_, vars) => {
      utils.clients.getNetworks.invalidate({ clientId: vars.clientId });
      utils.clients.getActiveConfig.invalidate();
      toast.success(vars.ativo ? "Rede ativada" : "Rede desativada");
    },
    onError: () => toast.error("Erro ao salvar rede"),
  });
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [newForm, setNewForm] = useState<NewForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editNome, setEditNome] = useState("");
  const [activeTab, setActiveTab] = useState("instancias");
  const handleSlugChange = (v: string) => {
    const slug = v.toLowerCase().replace(/\s/g, "-").replace(/[^a-z0-9-]/g, "");
    setNewForm(f => ({ ...f, slug, subdomain: slug }));
  };
  const canCreate = newForm.nome.trim() && newForm.slug.trim() && newForm.subdomain.trim();

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Acesso restrito a administradores RAZ.</p>
        </div>
      </div>
    );
  }

  const ativos = clients.filter(c => c.ativo);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* RAZ Mode Banner */}
      <div className="flex items-center gap-3 mb-6 px-4 py-3 rounded-xl border"
        style={{ background: "rgba(193,71,39,0.08)", borderColor: "rgba(193,71,39,0.25)" }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "#c14727" }}>
          <Shield className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold" style={{ color: "#c14727" }}>Modo RAZ</span>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white" style={{ background: "#c14727" }}>ADMIN</span>
          </div>
          <p className="text-xs text-muted-foreground">Painel de gestão exclusivo RAZ Consulting — instâncias, features e planos</p>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-xs text-muted-foreground">{ativos.length} ativo{ativos.length !== 1 ? "s" : ""}</p>
          <p className="text-xs text-muted-foreground">{clients.length} total</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 w-full sm:w-auto">
          <TabsTrigger value="instancias" className="flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" />Instâncias
          </TabsTrigger>
          <TabsTrigger value="features" className="flex items-center gap-1.5">
            <Settings2 className="w-3.5 h-3.5" />Features
          </TabsTrigger>
          <TabsTrigger value="planos" className="flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" />Planos
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-1.5">
            <Palette className="w-3.5 h-3.5" />Branding
          </TabsTrigger>
          <TabsTrigger value="flags" className="flex items-center gap-1.5">
            <ToggleRight className="w-3.5 h-3.5" />Feature Flags
          </TabsTrigger>
          <TabsTrigger value="api-usage" className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" />Uso de APIs
          </TabsTrigger>
          <TabsTrigger value="integracoes" className="flex items-center gap-1.5">
            <Plug className="w-3.5 h-3.5" />Integrações
          </TabsTrigger>
          <TabsTrigger value="linhas-editoriais" className="flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" />Linhas Editoriais
          </TabsTrigger>
          {/* "Gestão de APIs" removido — feature do monólito Manus (RELGOV/GIR), não pertence ao SocialHub */}
        </TabsList>

        {/* Aba: Instâncias */}
        <TabsContent value="instancias" className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold">Instâncias de Clientes</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Gerencie as empresas clientes e seus módulos</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setShowWizard(true)} className="gap-1.5">
                <Plus className="w-4 h-4" />Novo Cliente (guiado)
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowNew(v => !v)} className="gap-1.5" title="Criar cliente sem wizard — só campos básicos">
                Form simples
              </Button>
            </div>
          </div>
          {showNew && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <p className="text-sm font-semibold">Novo Cliente</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Nome do cliente *</label>
                  <Input placeholder="ex: EmiteAí" value={newForm.nome}
                    onChange={e => setNewForm(f => ({ ...f, nome: e.target.value }))} className="text-sm" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Slug (URL interna) *</label>
                  <Input placeholder="ex: emiteai" value={newForm.slug}
                    onChange={e => handleSlugChange(e.target.value)} className="text-sm" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Subdomínio *</label>
                  <div className="flex items-center gap-1">
                    <Input placeholder="emiteai" value={newForm.subdomain}
                      onChange={e => setNewForm(f => ({ ...f, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                      className="text-sm" />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">.razconsulting.com.br</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Cor primária</label>
                  <div className="flex gap-2">
                    <input type="color" value={newForm.primaryColor}
                      onChange={e => setNewForm(f => ({ ...f, primaryColor: e.target.value }))}
                      className="w-10 h-9 rounded border border-border cursor-pointer" />
                    <Input value={newForm.primaryColor}
                      onChange={e => setNewForm(f => ({ ...f, primaryColor: e.target.value }))}
                      className="text-sm flex-1" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Plano</label>
                  <Select value={newForm.plano} onValueChange={(v) => setNewForm(f => ({ ...f, plano: v as NewForm["plano"] }))}>
                    <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLANOS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Contato</label>
                  <Input placeholder="email ou telefone" value={newForm.contato}
                    onChange={e => setNewForm(f => ({ ...f, contato: e.target.value }))} className="text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground mb-1 block">Descrição</label>
                  <Input placeholder="Breve descrição do cliente" value={newForm.descricao}
                    onChange={e => setNewForm(f => ({ ...f, descricao: e.target.value }))} className="text-sm" />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={() => createClient.mutate(newForm)} disabled={!canCreate || createClient.isPending}>
                  {createClient.isPending ? "Criando..." : "Criar Cliente"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Total", value: clients.length },
              { label: "Ativos", value: ativos.length },
              { label: "Inativos", value: clients.filter(c => !c.ativo).length },
            ].map(s => (
              <div key={s.label} className="rounded-xl border border-border bg-card p-3 text-center">
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
          {isLoading ? (
            <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />)}</div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Nenhum cliente cadastrado</p>
              <p className="text-sm mt-1">Clique em "Novo Cliente" para começar</p>
            </div>
          ) : (
            <div className="space-y-3 group">
              {clients.map(client => (
                <ClientCard
                  key={client.id}
                  client={client}
                  expanded={expanded === client.id}
                  onToggle={() => setExpanded(prev => prev === client.id ? null : client.id)}
                  editingId={editingId}
                  editNome={editNome}
                  setEditingId={setEditingId}
                  setEditNome={setEditNome}
                  onUpdateNome={(id, nome) => updateClient.mutate({ id, nome })}
                  onToggleAtivo={(id, ativo) => updateClient.mutate({ id, ativo })}
                  setModule={(clientId, modulo, ativo) => setModule.mutate({ clientId, modulo, ativo })}
                  setNetwork={(clientId, rede, ativo) => setNetwork.mutate({ clientId, rede, ativo })}
                  onUpdateHomeModule={(id, homeModule) => updateClient.mutate({ id, homeModule })}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Aba: Features */}
        <TabsContent value="features" className="space-y-5">
          <div>
            <h2 className="text-base font-semibold">Catálogo de Features</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Módulos disponíveis no sistema e seus planos</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ALL_MODULES.map(mod => (
              <Card key={mod.key} className="border">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      {mod.key === "mirofish"
                        ? <Fish className="w-4 h-4 text-primary" />
                        : <Settings2 className="w-4 h-4 text-primary" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{mod.label}</p>
                      <p className="text-xs text-muted-foreground font-mono">{mod.key}</p>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {PLANOS.filter(p => p.modules.includes(mod.key)).map(p => (
                        <span key={p.value} className="text-xs px-1.5 py-0.5 rounded font-medium"
                          style={{ background: `${p.color}18`, color: p.color }}>
                          {p.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-sm font-semibold mb-2">Redes Sociais</p>
            <div className="flex flex-wrap gap-2">
              {ALL_NETWORKS.map(net => (
                <span key={net.key} className="text-xs px-2.5 py-1 rounded-full text-white font-medium"
                  style={{ backgroundColor: net.color }}>
                  {net.label}
                </span>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Aba: Planos */}
        <TabsContent value="planos" className="space-y-5">
          <div>
            <h2 className="text-base font-semibold">Definição de Planos</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Cada plano pré-define o conjunto de módulos disponíveis</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANOS.map(plano => {
              const Icon = plano.icon;
              const clientesNestePlano = clients.filter(c => (c.plano || "starter") === plano.value);
              return (
                <Card key={plano.value} className="border-t-4 overflow-hidden"
                  style={{ borderTopColor: plano.color }}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: `${plano.color}18` }}>
                        <Icon className="w-4 h-4" style={{ color: plano.color }} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{plano.label}</CardTitle>
                        <p className="text-xs text-muted-foreground">{clientesNestePlano.length} cliente{clientesNestePlano.length !== 1 ? "s" : ""}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 pb-4">
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Módulos incluídos</p>
                      {ALL_MODULES.map(mod => {
                        const included = plano.modules.includes(mod.key);
                        return (
                          <div key={mod.key} className={`flex items-center gap-2 text-xs ${included ? "text-foreground" : "text-muted-foreground/40"}`}>
                            <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 ${included ? "bg-green-100" : "bg-muted"}`}>
                              {included && <Check className="w-2.5 h-2.5 text-green-600" />}
                            </div>
                            {mod.label}
                          </div>
                        );
                      })}
                    </div>
                    {clientesNestePlano.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs font-semibold text-muted-foreground mb-1.5">Clientes</p>
                        <div className="flex flex-wrap gap-1">
                          {clientesNestePlano.map(c => (
                            <span key={c.id} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                              {c.nome}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="rounded-xl border border-border bg-muted/30 p-4">
            <div className="flex items-start gap-3">
              <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Como aplicar um plano</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Na aba <strong>Instâncias</strong>, expanda o card do cliente e ajuste os módulos individualmente.
                  O campo "Plano" define a categoria do cliente para fins de relatório e faturamento.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Aba: Branding */}
        <TabsContent value="branding" className="space-y-5">
          <BrandingTab clients={clients} />
        </TabsContent>

        {/* Aba: Feature Flags */}
        <TabsContent value="flags" className="space-y-5">
          <FeatureFlagsTab clients={clients} />
        </TabsContent>

        {/* Aba: Uso de APIs (F094) */}
        <TabsContent value="api-usage" className="space-y-5">
          <ApiUsageTab clients={clients} />
        </TabsContent>

        {/* Aba: Integrações (redes sociais + APIs externas como Reportei) */}
        <TabsContent value="integracoes" className="space-y-5">
          <IntegrationsTab clients={clients} />
        </TabsContent>

        {/* Aba: Linhas Editoriais por cliente (HU-073) */}
        <TabsContent value="linhas-editoriais" className="space-y-5">
          <EditorialLinesTab clients={clients} />
        </TabsContent>

      </Tabs>

      {/* Wizard de onboarding de novo cliente */}
      <NewClientWizard open={showWizard} onClose={() => setShowWizard(false)} />
    </div>
  );
}

// ─── Branding Tab ─────────────────────────────────────────────────────────────
function BrandingTab({ clients }: { clients: any[] }) {
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtractingPalette] = useState(false);
  const utils = trpc.useUtils();

  // Manual color state — pre-filled from DB or auto-fill from image
  const [primaryColor, setPrimaryColor] = useState("#c14727");
  const [secondaryColor, setSecondaryColor] = useState("#0a1628");
  const [accentColor, setAccentColor] = useState("#e91e8c");
  const [savingColors, setSavingColors] = useState(false);

  const selectedClient = clients.find(c => c.id === selectedClientId);

  const { data: brandingData } = trpc.branding.getByClientId.useQuery(
    { clientId: selectedClientId! },
    { enabled: !!selectedClientId }
  );

  // When branding data loads, pre-fill color pickers
  const prevClientId = useState<number | null>(null)[0];
  if (brandingData && selectedClientId !== prevClientId) {
    const p = brandingData.palette as Record<string, string> | null;
    if (p?.primary && p.primary !== primaryColor) setPrimaryColor(p.primary);
    if (p?.secondary && p.secondary !== secondaryColor) setSecondaryColor(p.secondary);
    if (p?.accent && p.accent !== accentColor) setAccentColor(p.accent);
  }

  // Invalida AMBAS as queries de branding (get + getByClientId) pra que
  // o hook useBranding (que usa .get) recarregue e re-aplique as CSS vars.
  function invalidateAllBranding() {
    utils.branding.getByClientId.invalidate();
    utils.branding.get.invalidate();
  }

  const uploadLogoMutation = trpc.branding.uploadLogo.useMutation({
    onSuccess: () => { invalidateAllBranding(); toast.success("Logo atualizado"); },
    onError: (e) => toast.error(e.message),
  });

  const extractPaletteMutation = trpc.branding.extractPalette.useMutation({
    onSuccess: (data) => {
      invalidateAllBranding();
      toast.success("Paleta extraída! Color pickers atualizados automaticamente.");
      setExtractingPalette(false);
      // Auto-fill color pickers from extracted palette
      const palette = data?.palette as Record<string, string> | undefined;
      if (palette?.primary) setPrimaryColor(palette.primary);
      if (palette?.secondary) setSecondaryColor(palette.secondary);
      if (palette?.accent) setAccentColor(palette.accent);
    },
    onError: (e) => { toast.error(e.message); setExtractingPalette(false); },
  });

  const savePaletteMutation = trpc.branding.savePalette.useMutation({
    onSuccess: () => { invalidateAllBranding(); toast.success("Paleta salva! Pra ver no app, troque pro cliente no switcher."); setSavingColors(false); },
    onError: (e) => { toast.error(e.message); setSavingColors(false); },
  });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: "icon" | "full") => {
    const file = e.target.files?.[0];
    if (!file || !selectedClientId) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = (ev.target?.result as string).split(",")[1];
      await uploadLogoMutation.mutateAsync({ clientId: selectedClientId, base64, mimeType: file.type, logoType: type });
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleExtractPalette = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClientId) return;
    setExtractingPalette(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = (ev.target?.result as string).split(",")[1];
      await extractPaletteMutation.mutateAsync({ clientId: selectedClientId, base64, mimeType: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleSaveColors = async () => {
    if (!selectedClientId) return;
    setSavingColors(true);
    await savePaletteMutation.mutateAsync({
      clientId: selectedClientId,
      palette: { primary: primaryColor, secondary: secondaryColor, accent: accentColor },
    });
  };

  const palette = brandingData?.palette as Record<string, string> | null;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold">Branding por Cliente</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Configure logo e paleta de cores para cada cliente</p>
      </div>

      {/* Client selector */}
      <Select value={selectedClientId?.toString() ?? ""} onValueChange={(v) => { setSelectedClientId(Number(v)); }}>
        <SelectTrigger className="w-64">
          <SelectValue placeholder="Selecionar cliente" />
        </SelectTrigger>
        <SelectContent>
          {clients.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.nome}</SelectItem>)}
        </SelectContent>
      </Select>

      {selectedClient && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Logo Upload */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Logos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Ícone (quadrado, ex: 64×64)</p>
                <div className="flex items-center gap-3">
                  {brandingData?.logoIconUrl && (
                    <img src={brandingData.logoIconUrl} alt="icon" className="h-10 w-10 rounded-lg object-contain border" />
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e, "icon")} />
                    <Button variant="outline" size="sm" asChild disabled={uploading}>
                      <span>{uploading ? "Enviando..." : "Upload ícone"}</span>
                    </Button>
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Logo completo (horizontal, ex: 200×50)</p>
                <div className="flex items-center gap-3">
                  {brandingData?.logoFullUrl && (
                    <img src={brandingData.logoFullUrl} alt="full" className="h-8 max-w-[160px] object-contain border rounded" />
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e, "full")} />
                    <Button variant="outline" size="sm" asChild disabled={uploading}>
                      <span>{uploading ? "Enviando..." : "Upload logo completo"}</span>
                    </Button>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Palette — manual + auto-fill */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Palette className="w-4 h-4" /> Paleta de Cores
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Auto-fill from image */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Auto-extrair de imagem</p>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={handleExtractPalette} />
                  <Button variant="outline" size="sm" asChild disabled={extracting} className="w-full">
                    <span className="flex items-center gap-2">
                      {extracting ? (
                        <><span className="animate-spin">&#9696;</span> Extraindo paleta...</>
                      ) : (
                        <>✨ Carregar imagem e auto-preencher cores</>
                      )}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground/70">O upload de uma imagem preenche automaticamente os campos abaixo</p>
              </div>

              <div className="border-t border-border/50 pt-3 space-y-3">
                <p className="text-xs font-medium text-muted-foreground">Definir manualmente</p>

                {/* Primary */}
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Cor Primária</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={e => setPrimaryColor(e.target.value)}
                      className="w-10 h-8 rounded cursor-pointer border border-border"
                    />
                    <Input
                      value={primaryColor}
                      onChange={e => setPrimaryColor(e.target.value)}
                      className="h-8 text-xs font-mono flex-1"
                      placeholder="#c14727"
                    />
                    <div className="w-8 h-8 rounded border flex-shrink-0" style={{ backgroundColor: primaryColor }} />
                  </div>
                </div>

                {/* Secondary */}
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Cor Secundária</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={secondaryColor}
                      onChange={e => setSecondaryColor(e.target.value)}
                      className="w-10 h-8 rounded cursor-pointer border border-border"
                    />
                    <Input
                      value={secondaryColor}
                      onChange={e => setSecondaryColor(e.target.value)}
                      className="h-8 text-xs font-mono flex-1"
                      placeholder="#0a1628"
                    />
                    <div className="w-8 h-8 rounded border flex-shrink-0" style={{ backgroundColor: secondaryColor }} />
                  </div>
                </div>

                {/* Accent */}
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Cor de Destaque (Accent)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={accentColor}
                      onChange={e => setAccentColor(e.target.value)}
                      className="w-10 h-8 rounded cursor-pointer border border-border"
                    />
                    <Input
                      value={accentColor}
                      onChange={e => setAccentColor(e.target.value)}
                      className="h-8 text-xs font-mono flex-1"
                      placeholder="#e91e8c"
                    />
                    <div className="w-8 h-8 rounded border flex-shrink-0" style={{ backgroundColor: accentColor }} />
                  </div>
                </div>

                {/* Live preview */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">Preview em tempo real</p>
                  <div className="rounded-xl border border-border p-3 space-y-2.5" style={{ background: secondaryColor }}>
                    {/* Simulated sidebar item */}
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: primaryColor }}>
                        <span className="text-white text-[10px] font-bold">A</span>
                      </div>
                      <span className="text-xs font-semibold" style={{ color: "#fff" }}>Nome do Cliente</span>
                    </div>
                    {/* Simulated nav item active */}
                    <div className="rounded-md px-2 py-1" style={{ background: `${primaryColor}33` }}>
                      <span className="text-xs font-medium" style={{ color: primaryColor === "#ffffff" ? "#000" : "#fff" }}>Dashboard</span>
                    </div>
                    {/* Simulated button + badge */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white" style={{ background: primaryColor }}>Botão</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white" style={{ background: accentColor }}>Badge</span>
                    </div>
                    {/* Color chips */}
                    <div className="flex gap-1">
                      <div className="flex-1 h-3 rounded" style={{ background: primaryColor }} title={`Primária: ${primaryColor}`} />
                      <div className="flex-1 h-3 rounded" style={{ background: secondaryColor }} title={`Secundária: ${secondaryColor}`} />
                      <div className="flex-1 h-3 rounded" style={{ background: accentColor }} title={`Accent: ${accentColor}`} />
                    </div>
                  </div>
                </div>

                <Button
                  size="sm"
                  className="w-full gap-1.5"
                  onClick={handleSaveColors}
                  disabled={savingColors || !selectedClientId}
                >
                  <Check className="w-3.5 h-3.5" />
                  {savingColors ? "Salvando..." : "Salvar Paleta"}
                </Button>
              </div>

              {/* Current saved palette chips */}
              {palette && (
                <div className="border-t border-border/50 pt-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Paleta salva no banco</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(palette).map(([key, value]) => (
                      <div key={key} className="space-y-1 cursor-pointer" onClick={() => {
                        if (key === "primary") setPrimaryColor(value);
                        if (key === "secondary") setSecondaryColor(value);
                        if (key === "accent") setAccentColor(value);
                      }}>
                        <div className="h-7 rounded border hover:ring-2 hover:ring-primary/40 transition-all" style={{ backgroundColor: value }} />
                        <p className="text-xs text-muted-foreground truncate">{key}</p>
                        <p className="text-xs font-mono">{value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground/60">Clique em uma cor salva para carregá-la nos pickers</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── Feature Flags Tab ────────────────────────────────────────────────────────
function FeatureFlagsTab({ clients }: { clients: any[] }) {
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const utils = trpc.useUtils();

  const { data: flags, isLoading } = trpc.featureFlags.list.useQuery(
    { clientId: selectedClientId! },
    { enabled: !!selectedClientId }
  );

  const toggleMutation = trpc.featureFlags.toggle.useMutation({
    onSuccess: () => utils.featureFlags.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const flagMap = Object.fromEntries((flags ?? []).map(f => [f.featureKey, f]));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold">Feature Flags por Cliente</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Habilite ou desabilite módulos individualmente por cliente</p>
      </div>

      <Select value={selectedClientId?.toString() ?? ""} onValueChange={(v) => setSelectedClientId(Number(v))}>
        <SelectTrigger className="w-64">
          <SelectValue placeholder="Selecionar cliente" />
        </SelectTrigger>
        <SelectContent>
          {clients.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.nome}</SelectItem>)}
        </SelectContent>
      </Select>

      {selectedClientId && (
        <div className="space-y-3">
          {ALL_MODULES.map(mod => {
            const flag = flagMap[mod.key];
            const enabled = flag ? flag.enabled : true; // default: enabled
            return (
              <div key={mod.key} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div>
                  <p className="text-sm font-medium">{mod.label}</p>
                  <p className="text-xs text-muted-foreground font-mono">{mod.key}</p>
                </div>
                <button
                  onClick={() => toggleMutation.mutate({ clientId: selectedClientId, featureKey: mod.key, enabled: !enabled })}
                  disabled={toggleMutation.isPending}
                  className="transition-opacity"
                >
                  {enabled
                    ? <ToggleRight className="w-8 h-8 text-green-500" />
                    : <ToggleLeft className="w-8 h-8 text-muted-foreground" />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── API Usage Tab (F094) ─────────────────────────────────────────────────────
const FONTE_LABELS: Record<string, { label: string; limit?: number; unit?: string }> = {
  newsapi:         { label: "NewsAPI",          limit: 100,  unit: "req/dia" },
  alpha_vantage:   { label: "Alpha Vantage",    limit: 25,   unit: "req/dia" },
  firecrawl:       { label: "Firecrawl",        limit: 500,  unit: "req/mês" },
  camara:          { label: "Câmara Federal",   unit: "req" },
  open_sanctions:  { label: "Open Sanctions",   unit: "req" },
  rss:             { label: "RSS Feeds",        unit: "req" },
  reddit:          { label: "Reddit",           unit: "req" },
};

function ApiUsageTab({ clients }: { clients: any[] }) {
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const { data: usage, isLoading } = trpc.clients.getApiUsage.useQuery(
    { clientId: selectedClientId! },
    { enabled: !!selectedClientId }
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold">Uso de APIs por Cliente</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Coletas dos últimos 30 dias — dados reais de gir_coleta_log e api_keys</p>
      </div>
      <Select value={selectedClientId?.toString() ?? ""} onValueChange={v => setSelectedClientId(Number(v))}>
        <SelectTrigger className="w-64 text-sm">
          <SelectValue placeholder="Selecionar cliente..." />
        </SelectTrigger>
        <SelectContent>
          {clients.map(c => (
            <SelectItem key={c.id} value={c.id.toString()} className="text-xs">{c.nome}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isLoading && (
        <div className="space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />)}
        </div>
      )}

      {usage && !isLoading && (
        <div className="space-y-4">
          {/* API Keys configuradas */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Chaves de API Configuradas</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {usage.apis.length === 0 && (
                <p className="text-sm text-muted-foreground col-span-2">Nenhuma chave configurada para este cliente.</p>
              )}
              {usage.apis.map((api: any) => (
                <Card key={api.id} className="border">
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${api.ativa ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{api.nome}</p>
                      <p className="text-xs text-muted-foreground">{api.descricao ?? api.plataforma}</p>
                    </div>
                    <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{api.envVar ?? api.plataforma}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Coletas dos últimos 30 dias */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Coletas — Últimos 30 Dias</p>
            {Object.keys(usage.byFonte).length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhuma coleta registrada nos últimos 30 dias.</p>
            )}
            <div className="space-y-2">
              {Object.entries(usage.byFonte as Record<string, { runs: number; successRuns: number; totalInputs: number; lastRun: string | null }>).map(([fonte, stats]) => {
                const meta = FONTE_LABELS[fonte] ?? { label: fonte, unit: "req" };
                const successRate = stats.runs > 0 ? Math.round((stats.successRuns / stats.runs) * 100) : 0;
                const barWidth = meta.limit ? Math.min(100, Math.round((stats.runs / meta.limit) * 100)) : null;
                return (
                  <div key={fonte} className="rounded-lg border bg-card p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{meta.label}</p>
                        <p className="text-xs text-muted-foreground font-mono">{fonte}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{stats.runs} runs</p>
                        <p className="text-xs text-muted-foreground">{stats.totalInputs} inputs coletados</p>
                      </div>
                    </div>
                    {barWidth !== null && (
                      <div>
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Uso estimado</span>
                          <span>{barWidth}% do limite ({meta.limit} {meta.unit})</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${barWidth > 80 ? 'bg-red-500' : barWidth > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Taxa de sucesso: <span className={`font-medium ${successRate >= 90 ? 'text-green-600' : successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>{successRate}%</span></span>
                      {stats.lastRun && <span>Última coleta: {new Date(stats.lastRun).toLocaleString('pt-BR')}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Editorial Lines Tab ─────────────────────────────────────────────────────
// CRUD das linhas editoriais por cliente. As mesmas linhas aparecem em
// Pautas/Pecas/Insights/BancoIdeias/LinhaEditorial via useEditorialLines hook.
//
// Fluxos:
// - Cliente novo (sem linhas): "Semear template do EmiteAí" ou "Criar do zero"
// - Cliente com linhas: cards expansíveis pra editar cada linha, + Nova, soft delete
function EditorialLinesTab({ clients }: { clients: any[] }) {
  const ativos = (clients ?? []).filter((c: any) => c.ativo !== false && c.deletedAt == null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const utils = trpc.useUtils();
  const linesQuery = trpc.clients.getEditorialLines.useQuery(
    { clientId: selectedClientId ?? 0 },
    { enabled: !!selectedClientId }
  );
  const upsertMut = trpc.clients.upsertEditorialLine.useMutation({
    onSuccess: () => { toast.success("Linha salva"); linesQuery.refetch(); },
    onError: (e) => toast.error(e.message ?? "Falha ao salvar"),
  });
  const deleteMut = trpc.clients.deleteEditorialLine.useMutation({
    onSuccess: () => { toast.success("Linha removida"); linesQuery.refetch(); },
    onError: (e) => toast.error(e.message ?? "Falha ao remover"),
  });
  const seedMut = trpc.clients.seedDefaultEditorialLines.useMutation({
    onSuccess: (res: any) => {
      if (res?.success) toast.success("Template aplicado");
      else toast.info("Cliente já tem linhas configuradas");
      linesQuery.refetch();
    },
    onError: (e) => toast.error(e.message ?? "Falha ao semear"),
  });

  const lines = (linesQuery.data ?? []) as any[];
  const selectedClient = ativos.find((c: any) => c.id === selectedClientId);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2">
          <BookOpen className="w-4 h-4" />Linhas Editoriais
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Cada cliente tem suas próprias linhas editoriais — pilares, frequência, formatos, personas.
          Editadas aqui, aparecem automaticamente em Pautas, Peças, Insights, Banco de Ideias e na página Linha Editorial.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <label className="text-xs text-muted-foreground mb-1.5 block">Cliente</label>
        <Select
          value={selectedClientId?.toString() ?? ""}
          onValueChange={(v) => setSelectedClientId(Number(v))}
        >
          <SelectTrigger className="text-sm h-10 max-w-md"><SelectValue placeholder="Selecione um cliente…" /></SelectTrigger>
          <SelectContent>
            {ativos.map((c: any) => (
              <SelectItem key={c.id} value={c.id.toString()}>{c.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedClientId && (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Selecione um cliente acima pra editar as linhas editoriais dele.</p>
        </div>
      )}

      {selectedClientId && linesQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Carregando linhas…</p>
      )}

      {selectedClientId && !linesQuery.isLoading && lines.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center space-y-3">
          <Sparkles className="w-10 h-10 mx-auto text-muted-foreground" />
          <div>
            <p className="text-sm font-semibold">Nenhuma linha editorial configurada</p>
            <p className="text-xs text-muted-foreground mt-1">
              {selectedClient?.nome ?? "Esse cliente"} ainda não tem linhas. Sugerido: semear a partir do template do EmiteAí e ajustar.
            </p>
          </div>
          <div className="flex justify-center gap-2 pt-2">
            <Button
              size="sm"
              onClick={() => seedMut.mutate({ clientId: selectedClientId, templateFromClientId: 1 })}
              disabled={seedMut.isPending}
              className="gap-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {seedMut.isPending ? "Semeando…" : "Semear template do EmiteAí (6 linhas)"}
            </Button>
            <NovaLinhaButton clientId={selectedClientId} ordem={0} onSaved={() => linesQuery.refetch()} />
          </div>
        </div>
      )}

      {selectedClientId && lines.length > 0 && (
        <>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Distribuição ({lines.length} {lines.length === 1 ? "linha" : "linhas"})
              </p>
              <span className="text-xs text-muted-foreground">
                Soma das frequências: {lines.reduce((a: number, l: any) => a + (l.frequenciaPct || 0), 0)}%
              </span>
            </div>
            <div className="flex gap-1 h-7 rounded-lg overflow-hidden">
              {lines.map((l: any) => (
                <div
                  key={l.id}
                  className="flex items-center justify-center text-[10px] font-bold text-white px-1"
                  style={{ background: l.color, flex: Math.max(l.frequenciaPct || 0, 1) }}
                  title={`${l.codigo} ${l.nome}: ${l.frequenciaPct}%`}
                >
                  {l.codigo}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {lines.map((line: any) => (
              <LinhaEditorialCard
                key={line.id}
                line={line}
                clientId={selectedClientId}
                onSave={(payload) => upsertMut.mutate(payload)}
                onDelete={() => {
                  if (confirm(`Remover a linha "${line.codigo} ${line.nome}"? (Pode ser restaurada via banco depois)`)) {
                    deleteMut.mutate({ id: line.id, clientId: selectedClientId });
                  }
                }}
                isSaving={upsertMut.isPending}
              />
            ))}
          </div>

          <div className="flex justify-center pt-2">
            <NovaLinhaButton
              clientId={selectedClientId}
              ordem={Math.max(0, ...lines.map((l: any) => l.ordem ?? 0)) + 1}
              onSaved={() => { utils.clients.getEditorialLines.invalidate(); linesQuery.refetch(); }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function NovaLinhaButton({ clientId, ordem, onSaved }: { clientId: number; ordem: number; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const upsertMut = trpc.clients.upsertEditorialLine.useMutation({
    onSuccess: () => { toast.success("Linha criada"); setOpen(false); onSaved(); },
    onError: (e) => toast.error(e.message ?? "Falha ao criar"),
  });

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1.5">
        <Plus className="w-3.5 h-3.5" />Nova linha
      </Button>
    );
  }

  return (
    <div className="w-full">
      <LinhaEditorialCard
        line={{
          id: undefined,
          codigo: `L${ordem + 1}`,
          nome: "",
          color: "#0A0A0A",
          bg: "#EDEBE6",
          descricao: "",
          objetivo: "",
          frequenciaPct: 0,
          tom: "",
          pilares: [],
          formatos: [],
          personas: [],
          exemplos: [],
          mixSemanal: "",
          ordem,
        }}
        clientId={clientId}
        startInEdit
        onSave={(payload) => upsertMut.mutate(payload)}
        onDelete={() => setOpen(false)}
        isSaving={upsertMut.isPending}
      />
    </div>
  );
}

function LinhaEditorialCard({
  line, clientId, onSave, onDelete, isSaving, startInEdit = false,
}: {
  line: any;
  clientId: number;
  onSave: (payload: any) => void;
  onDelete: () => void;
  isSaving: boolean;
  startInEdit?: boolean;
}) {
  const [editing, setEditing] = useState(startInEdit);
  const [form, setForm] = useState({
    codigo:        line.codigo ?? "",
    nome:          line.nome ?? "",
    color:         line.color ?? "#0A0A0A",
    bg:            line.bg ?? "#EDEBE6",
    descricao:     line.descricao ?? "",
    objetivo:      line.objetivo ?? "",
    frequenciaPct: line.frequenciaPct ?? 0,
    tom:           line.tom ?? "",
    pilares:       (line.pilares ?? []).join(", "),
    formatos:      (line.formatos ?? []).join(", "),
    personas:      (line.personas ?? []).join(", "),
    exemplos:      (line.exemplos ?? []).join("\n"),
    mixSemanal:    line.mixSemanal ?? "",
    ordem:         line.ordem ?? 0,
  });

  function set<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  function csvToArray(s: string): string[] {
    return s.split(",").map(x => x.trim()).filter(Boolean);
  }
  function linesToArray(s: string): string[] {
    return s.split(/\n+/).map(x => x.trim()).filter(Boolean);
  }

  function handleSave() {
    if (!form.codigo.trim() || !form.nome.trim()) {
      toast.error("Código e nome são obrigatórios");
      return;
    }
    onSave({
      id: line.id,
      clientId,
      ordem: Number(form.ordem) || 0,
      codigo: form.codigo.trim(),
      nome: form.nome.trim(),
      color: form.color,
      bg: form.bg,
      descricao: form.descricao || undefined,
      objetivo: form.objetivo || undefined,
      frequenciaPct: Number(form.frequenciaPct) || 0,
      tom: form.tom || undefined,
      pilares: csvToArray(form.pilares),
      formatos: csvToArray(form.formatos),
      personas: csvToArray(form.personas),
      exemplos: linesToArray(form.exemplos),
      mixSemanal: form.mixSemanal || undefined,
    });
    setEditing(false);
  }

  if (!editing) {
    return (
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 flex items-center gap-3" style={{ background: line.bg }}>
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs shrink-0"
            style={{ background: line.color }}
          >
            {line.codigo}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate" style={{ color: line.color }}>{line.nome}</p>
            <p className="text-[11px]" style={{ color: line.color + "AA" }}>
              {line.frequenciaPct}% do calendário {line.tom ? `· ${line.tom}` : ""}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="gap-1.5 shrink-0">
            <Pencil className="w-3 h-3" />Editar
          </Button>
        </div>
        {(line.descricao || (line.pilares ?? []).length > 0) && (
          <div className="px-4 py-3 space-y-2 text-xs text-muted-foreground border-t border-border">
            {line.descricao && <p>{line.descricao}</p>}
            {(line.pilares ?? []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {(line.pilares as string[]).map((p, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: line.bg, color: line.color }}>
                    {p}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-foreground bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold flex items-center gap-2">
          <Pencil className="w-3.5 h-3.5" />
          {line.id ? `Editando ${line.codigo}` : "Nova linha editorial"}
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); if (!line.id) onDelete(); }}>Cancelar</Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving} className="gap-1.5">
            <Check className="w-3.5 h-3.5" />{isSaving ? "Salvando…" : "Salvar"}
          </Button>
          {line.id && (
            <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5">
              <Trash2 className="w-3.5 h-3.5" />Remover
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-2">
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Código *</label>
          <Input value={form.codigo} onChange={e => set("codigo", e.target.value.toUpperCase())} placeholder="L1" className="text-sm" />
        </div>
        <div className="col-span-7">
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Nome *</label>
          <Input value={form.nome} onChange={e => set("nome", e.target.value)} placeholder="Ex: Compliance & Controle" className="text-sm" />
        </div>
        <div className="col-span-3">
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Frequência %</label>
          <Input type="number" min={0} max={100} value={form.frequenciaPct}
            onChange={e => set("frequenciaPct", Number(e.target.value) as any)} className="text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Cor primária</label>
          <div className="flex gap-2">
            <input type="color" value={form.color} onChange={e => set("color", e.target.value)}
              className="w-9 h-9 rounded border border-border cursor-pointer shrink-0" />
            <Input value={form.color} onChange={e => set("color", e.target.value)} className="text-sm font-mono" />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Cor de fundo (chip)</label>
          <div className="flex gap-2">
            <input type="color" value={form.bg} onChange={e => set("bg", e.target.value)}
              className="w-9 h-9 rounded border border-border cursor-pointer shrink-0" />
            <Input value={form.bg} onChange={e => set("bg", e.target.value)} className="text-sm font-mono" />
          </div>
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Descrição</label>
        <textarea value={form.descricao} onChange={e => set("descricao", e.target.value)}
          rows={2} placeholder="Pra que serve essa linha"
          className="w-full text-sm rounded-md border border-input bg-transparent px-3 py-2 shadow-sm" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Objetivo</label>
          <Input value={form.objetivo} onChange={e => set("objetivo", e.target.value)}
            placeholder="Qual KPI/comportamento isso ataca" className="text-sm" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Tom</label>
          <Input value={form.tom} onChange={e => set("tom", e.target.value)}
            placeholder="Ex: Técnico, próximo, provocativo" className="text-sm" />
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">
          Pilares <span className="text-muted-foreground/70">(separe por vírgula)</span>
        </label>
        <Input value={form.pilares} onChange={e => set("pilares", e.target.value)}
          placeholder="Pilar 1, Pilar 2, Pilar 3" className="text-sm" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">
            Formatos recomendados <span className="text-muted-foreground/70">(vírgula)</span>
          </label>
          <Input value={form.formatos} onChange={e => set("formatos", e.target.value)}
            placeholder="Carrossel, Reels, Post estático" className="text-sm" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">
            Personas <span className="text-muted-foreground/70">(vírgula)</span>
          </label>
          <Input value={form.personas} onChange={e => set("personas", e.target.value)}
            placeholder="CFO de PME, Operação fiscal" className="text-sm" />
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">
          Exemplos de ângulos <span className="text-muted-foreground/70">(um por linha)</span>
        </label>
        <textarea value={form.exemplos} onChange={e => set("exemplos", e.target.value)}
          rows={3}
          placeholder={"Ex: Como cortar 30% do tempo de fechamento fiscal\nEx: O que CFOs estão fazendo errado em SaaS"}
          className="w-full text-sm rounded-md border border-input bg-transparent px-3 py-2 shadow-sm" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Mix semanal</label>
          <Input value={form.mixSemanal} onChange={e => set("mixSemanal", e.target.value)}
            placeholder="Ex: 2 posts/semana" className="text-sm" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Ordem</label>
          <Input type="number" value={form.ordem}
            onChange={e => set("ordem", Number(e.target.value) as any)} className="text-sm" />
        </div>
      </div>
    </div>
  );
}

// ─── Integrations Tab ────────────────────────────────────────────────────────
// Onde o admin conecta as redes sociais e APIs externas (Reportei) do cliente.
//
// Estrutura:
// 1) Redes sociais: 4 cards (IG, LI, TT, YT) — toggle ativo + @handle + URL +
//    Account ID + Page ID (só IG) + notas
// 2) Reportei: form pra edit do reportei_client_config (api_token, project_id,
//    integration IDs e handles por rede)
//
// (Outros connectors — Meta Business API direta, LinkedIn API, etc — entram
// aqui quando forem necessários. Hoje toda métrica vem via Reportei.)
const REDES_INFO: { key: string; label: string; color: string; bg: string; icon: any; urlExample: string; placeholder: string }[] = [
  { key: "instagram", label: "Instagram", color: "#E1306C", bg: "#FDE8F5", icon: Instagram,
    urlExample: "https://instagram.com/emiteai", placeholder: "@emiteai" },
  { key: "linkedin",  label: "LinkedIn",  color: "#0A66C2", bg: "#DBEAFE", icon: Linkedin,
    urlExample: "https://linkedin.com/company/emiteai", placeholder: "emiteai" },
  { key: "tiktok",    label: "TikTok",    color: "#0A0A0A", bg: "#E6FBF4", icon: Globe,
    urlExample: "https://tiktok.com/@emiteai", placeholder: "@emiteai" },
  { key: "youtube",   label: "YouTube",   color: "#FF0000", bg: "#FEE2E2", icon: Youtube,
    urlExample: "https://youtube.com/@emiteai", placeholder: "@emiteai" },
];

function IntegrationsTab({ clients }: { clients: any[] }) {
  const ativos = (clients ?? []).filter((c: any) => c.ativo !== false && c.deletedAt == null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const selectedClient = ativos.find((c: any) => c.id === selectedClientId);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Plug className="w-4 h-4" />Integrações do cliente
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Configure as contas das redes sociais e as APIs externas (Reportei) usadas pra alimentar dados desse cliente.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <label className="text-xs text-muted-foreground mb-1.5 block">Cliente</label>
        <Select
          value={selectedClientId?.toString() ?? ""}
          onValueChange={(v) => setSelectedClientId(Number(v))}
        >
          <SelectTrigger className="text-sm h-10 max-w-md"><SelectValue placeholder="Selecione um cliente…" /></SelectTrigger>
          <SelectContent>
            {ativos.map((c: any) => (
              <SelectItem key={c.id} value={c.id.toString()}>{c.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedClientId && (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <Plug className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Selecione um cliente acima pra configurar as integrações dele.</p>
        </div>
      )}

      {selectedClientId && (
        <>
          <SocialNetworksSection clientId={selectedClientId} clientName={selectedClient?.nome} />
          <ReporteiSection clientId={selectedClientId} />
        </>
      )}
    </div>
  );
}

function SocialNetworksSection({ clientId, clientName }: { clientId: number; clientName?: string }) {
  const networksQuery = trpc.clients.getNetworks.useQuery({ clientId });
  const setNetworkMut = trpc.clients.setNetwork.useMutation({
    onSuccess: () => networksQuery.refetch(),
  });
  const upsertCfgMut = trpc.clients.upsertNetworkConfig.useMutation({
    onSuccess: () => { toast.success("Rede atualizada"); networksQuery.refetch(); },
    onError: (e) => toast.error(e.message ?? "Falha ao salvar"),
  });

  const networks = (networksQuery.data ?? []) as any[];
  const byRede = new Map<string, any>();
  for (const n of networks) byRede.set(n.rede, n);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Wifi className="w-4 h-4" />Redes sociais
        </h3>
        <p className="text-xs text-muted-foreground">{clientName ?? ""}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {REDES_INFO.map(info => {
          const cfg = byRede.get(info.key) ?? { rede: info.key, ativo: false };
          return (
            <NetworkConfigCard
              key={info.key}
              info={info}
              cfg={cfg}
              onToggleActive={(ativo) => setNetworkMut.mutate({ clientId, rede: info.key, ativo })}
              onSave={(payload) => upsertCfgMut.mutate({ clientId, rede: info.key, ...payload })}
              isSaving={upsertCfgMut.isPending}
            />
          );
        })}
      </div>
    </div>
  );
}

function NetworkConfigCard({
  info, cfg, onToggleActive, onSave, isSaving,
}: {
  info: typeof REDES_INFO[number];
  cfg: any;
  onToggleActive: (ativo: boolean) => void;
  onSave: (payload: any) => void;
  isSaving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [handle, setHandle]       = useState(cfg.handle ?? "");
  const [url, setUrl]             = useState(cfg.url ?? "");
  const [accountId, setAccountId] = useState(cfg.accountId ?? "");
  const [pageId, setPageId]       = useState(cfg.pageId ?? "");
  const [notes, setNotes]         = useState(cfg.notes ?? "");

  // Sincroniza quando cfg muda (depois do refetch)
  function resetFromCfg() {
    setHandle(cfg.handle ?? "");
    setUrl(cfg.url ?? "");
    setAccountId(cfg.accountId ?? "");
    setPageId(cfg.pageId ?? "");
    setNotes(cfg.notes ?? "");
  }

  const Icon = info.icon;
  const ativo = !!cfg.ativo;
  const temConfig = !!(cfg.handle || cfg.url || cfg.accountId);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-3" style={{ background: info.bg }}>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: info.color }}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm" style={{ color: info.color }}>{info.label}</p>
          <p className="text-[11px] truncate" style={{ color: info.color + "AA" }}>
            {ativo ? (cfg.handle ? `${cfg.handle} · ativa` : "Ativa · sem @ configurado") : "Inativa"}
          </p>
        </div>
        <button
          onClick={() => onToggleActive(!ativo)}
          className="shrink-0"
          title={ativo ? "Desativar" : "Ativar"}
        >
          {ativo ? <ToggleRight className="w-7 h-7" style={{ color: info.color }} /> : <ToggleLeft className="w-7 h-7 text-zinc-400" />}
        </button>
      </div>

      {!editing && (
        <div className="px-4 py-3 border-t border-border space-y-2 text-xs">
          {temConfig ? (
            <>
              {cfg.handle && <div className="flex items-center gap-2"><span className="text-muted-foreground">@:</span><span className="font-medium">{cfg.handle}</span></div>}
              {cfg.url && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">URL:</span>
                  <a href={cfg.url} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline truncate flex items-center gap-1" style={{ color: info.color }}>
                    {cfg.url} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              {cfg.accountId && <div className="flex items-center gap-2"><span className="text-muted-foreground">Account ID:</span><span className="font-mono">{cfg.accountId}</span></div>}
              {info.key === "instagram" && cfg.pageId && <div className="flex items-center gap-2"><span className="text-muted-foreground">Page ID:</span><span className="font-mono">{cfg.pageId}</span></div>}
              {cfg.notes && <div className="text-muted-foreground italic">{cfg.notes}</div>}
            </>
          ) : (
            <p className="text-muted-foreground italic">Nenhuma configuração — clique em "Configurar"</p>
          )}
          <div className="flex justify-end pt-1">
            <Button size="sm" variant="outline" onClick={() => { resetFromCfg(); setEditing(true); }} className="gap-1.5 h-7 text-xs">
              <Pencil className="w-3 h-3" />Configurar
            </Button>
          </div>
        </div>
      )}

      {editing && (
        <div className="px-4 py-3 border-t border-border space-y-3 bg-muted/30">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">@ Handle</label>
            <Input value={handle} onChange={e => setHandle(e.target.value)} placeholder={info.placeholder} className="text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">URL pública</label>
            <Input value={url} onChange={e => setUrl(e.target.value)} placeholder={info.urlExample} className="text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Account ID</label>
              <Input value={accountId} onChange={e => setAccountId(e.target.value)} placeholder="opcional" className="text-sm font-mono" />
            </div>
            {info.key === "instagram" && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Page ID (Meta)</label>
                <Input value={pageId} onChange={e => setPageId(e.target.value)} placeholder="opcional" className="text-sm font-mono" />
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Notas</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Ex: conta gerenciada pela agência X, IG biz, etc."
              className="w-full text-sm rounded-md border border-input bg-transparent px-3 py-2 shadow-sm" />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => { resetFromCfg(); setEditing(false); }}>Cancelar</Button>
            <Button size="sm" disabled={isSaving} onClick={() => {
              onSave({
                handle:    handle || null,
                url:       url || null,
                accountId: accountId || null,
                pageId:    info.key === "instagram" ? (pageId || null) : null,
                notes:     notes || null,
              });
              setEditing(false);
            }} className="gap-1.5">
              <Check className="w-3.5 h-3.5" />{isSaving ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReporteiSection({ clientId }: { clientId: number }) {
  const cfgQuery = trpc.clients.getReporteiConfig.useQuery({ clientId });
  const upsertMut = trpc.clients.upsertReporteiConfig.useMutation({
    onSuccess: () => { toast.success("Reportei atualizado"); cfgQuery.refetch(); },
    onError: (e) => toast.error(e.message ?? "Falha ao salvar"),
  });

  const cfg = cfgQuery.data;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    apiToken: "", projectId: "",
    igHandle: "", liHandle: "", ttHandle: "", ytHandle: "",
    igIntegrationId: "", liIntegrationId: "", ttIntegrationId: "", ytIntegrationId: "",
  });

  function loadFromCfg() {
    if (!cfg) {
      setForm({ apiToken: "", projectId: "", igHandle: "", liHandle: "", ttHandle: "", ytHandle: "",
        igIntegrationId: "", liIntegrationId: "", ttIntegrationId: "", ytIntegrationId: "" });
      return;
    }
    setForm({
      apiToken:         cfg.apiToken ?? "",
      projectId:        cfg.projectId ?? "",
      igHandle:         cfg.igHandle ?? "",
      liHandle:         cfg.liHandle ?? "",
      ttHandle:         cfg.ttHandle ?? "",
      ytHandle:         cfg.ytHandle ?? "",
      igIntegrationId:  cfg.igIntegrationId ?? "",
      liIntegrationId:  cfg.liIntegrationId ?? "",
      ttIntegrationId:  cfg.ttIntegrationId ?? "",
      ytIntegrationId:  cfg.ytIntegrationId ?? "",
    });
  }

  const hasConfig = !!(cfg?.apiToken || cfg?.projectId);

  return (
    <div className="space-y-3 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />Reportei
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">fonte de métricas</span>
        </h3>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => { loadFromCfg(); setEditing(true); }} className="gap-1.5">
            <Pencil className="w-3 h-3" />{hasConfig ? "Editar" : "Configurar"}
          </Button>
        )}
      </div>

      {!editing && (
        <div className="rounded-xl border border-border bg-card p-4">
          {hasConfig ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">API Token</p>
                <p className="font-mono">{cfg?.apiToken ? `${cfg.apiToken.slice(0, 8)}••••••` : "—"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Project ID</p>
                <p className="font-mono">{cfg?.projectId ?? "—"}</p>
              </div>
              <div className="md:col-span-2 pt-2 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { key: "ig", label: "Instagram", handle: cfg?.igHandle, id: cfg?.igIntegrationId },
                  { key: "li", label: "LinkedIn",  handle: cfg?.liHandle, id: cfg?.liIntegrationId },
                  { key: "tt", label: "TikTok",    handle: cfg?.ttHandle, id: cfg?.ttIntegrationId },
                  { key: "yt", label: "YouTube",   handle: cfg?.ytHandle, id: cfg?.ytIntegrationId },
                ].map(r => (
                  <div key={r.key}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">{r.label}</p>
                    <p className="font-medium">{r.handle || "—"}</p>
                    <p className="text-[10px] font-mono text-muted-foreground">{r.id || "sem integration ID"}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">Reportei ainda não configurado pra este cliente.</p>
          )}
        </div>
      )}

      {editing && (
        <div className="rounded-xl border-2 border-foreground bg-card p-4 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">API Token Reportei</label>
            <Input type="password" value={form.apiToken}
              onChange={e => setForm(f => ({ ...f, apiToken: e.target.value }))}
              placeholder="reportei_xxxxxx" className="text-sm font-mono" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Project ID</label>
            <Input value={form.projectId}
              onChange={e => setForm(f => ({ ...f, projectId: e.target.value }))}
              placeholder="proj_xxxxxx" className="text-sm font-mono" />
          </div>
          <div className="pt-2 border-t border-border space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Por rede</p>
            {[
              { key: "ig", label: "Instagram" },
              { key: "li", label: "LinkedIn" },
              { key: "tt", label: "TikTok" },
              { key: "yt", label: "YouTube" },
            ].map(r => (
              <div key={r.key} className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-medium text-muted-foreground mb-1 block">@ {r.label}</label>
                  <Input value={(form as any)[`${r.key}Handle`]}
                    onChange={e => setForm(f => ({ ...f, [`${r.key}Handle`]: e.target.value }))}
                    placeholder="@cliente" className="text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-muted-foreground mb-1 block">Integration ID</label>
                  <Input value={(form as any)[`${r.key}IntegrationId`]}
                    onChange={e => setForm(f => ({ ...f, [`${r.key}IntegrationId`]: e.target.value }))}
                    placeholder="opcional" className="text-sm font-mono" />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>
            <Button size="sm" disabled={upsertMut.isPending} onClick={() => {
              upsertMut.mutate({
                clientId,
                apiToken:         form.apiToken || null,
                projectId:        form.projectId || null,
                igHandle:         form.igHandle || null,
                liHandle:         form.liHandle || null,
                ttHandle:         form.ttHandle || null,
                ytHandle:         form.ytHandle || null,
                igIntegrationId:  form.igIntegrationId || null,
                liIntegrationId:  form.liIntegrationId || null,
                ttIntegrationId:  form.ttIntegrationId || null,
                ytIntegrationId:  form.ytIntegrationId || null,
              });
              setEditing(false);
            }} className="gap-1.5">
              <Check className="w-3.5 h-3.5" />{upsertMut.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
